import { DEFAULT_SEARCH_STATE } from "@/contexts/SearchContext/constants";
import {
  PropertyListingCategory,
  PropertyListingSearchReferrer,
} from "@/contexts/SearchContext/enums";
import { SearchState } from "@/contexts/SearchContext/interfaces";
import { PropertyListingType, PropertySorting } from "@/enums/property.enum";
import { Property } from "@/interfaces/property.interface";
import { orderBy } from "lodash";

export const checkIfDefaultState = (state: SearchState) =>
  JSON.stringify(orderBy(state)) ===
  JSON.stringify(orderBy(DEFAULT_SEARCH_STATE));

export const getListingId = (
  state: SearchState,
  trigger: string
): PropertyListingCategory => {
  let where = PropertyListingCategory.FILTERED;

  if (trigger === "general") {
    if (state.referrer === PropertyListingSearchReferrer.HOMEPAGE) {
      // where its site search or filtered

      if (state.locations.length > 0 && checkIfDefaultState(state)) {
        where = PropertyListingCategory.SITE_SEARCH;
      }
    }
  } else if (trigger === "sort") {
    switch (state.sort) {
      case PropertySorting.AREA_HIGH_TO_LOW:
        where = PropertyListingCategory.SORTING_AREA_HIGH_TO_LOW;
        break;
      case PropertySorting.AREA_LOW_TO_HIGH:
        where = PropertyListingCategory.SORTING_AREA_LOW_TO_HIGH;
        break;
      case PropertySorting.PRICE_HIGH_TO_LOW:
        where = PropertyListingCategory.SORTING_PRICE_HIGH_TO_LOW;
        break;
      case PropertySorting.PRICE_LOW_TO_HIGH:
        where = PropertyListingCategory.SORTING_PRICE_LOW_TO_HIGH;
        break;
      case PropertySorting.DATE_NEW_TO_OLD:
        where = PropertyListingCategory.SORTING_DATE_NEWEST_TO_OLDEST;
        break;
      case PropertySorting.DATE_OLD_TO_NEW:
        where = PropertyListingCategory.SORTING_DATE_OLDEST_TO_NEWEST;
        break;
    }
  } else if (trigger === "type") {
    switch (state.listing.type) {
      case PropertyListingType.BUY:
        where = PropertyListingCategory.CATEGORY_BUY;
        break;
      case PropertyListingType.RENT:
        where = PropertyListingCategory.CATEGORY_RENT;
        break;
      case PropertyListingType.COMMERCIAL_BUY:
        where = PropertyListingCategory.CATEGORY_COMMERCIAL_BUY;
        break;
      case PropertyListingType.COMMERCIAL_RENT:
        where = PropertyListingCategory.CATEGORY_COMMERCIAL_RENT;
        break;
      case PropertyListingType.OFF_PLAN:
        where = PropertyListingCategory.CATEGORY_OFF_PLAN;
        break;
    }
  }

  return where;
};

export const stateToViewItemList = (state: SearchState) => {
  return {
    search_term: state.locations.map((location) => location.label).join(", "),
    property_category: state.listing.type,
    property_type: state.listing.property,
    badges_status: (state.badges || []).length > 0,
    bedroom_count: state.bedrooms?.join(","),
    bathroom_count: state.bathrooms?.join(","),
    price_range: `${state.price.from ?? "any"} - ${state.price.to ?? "any"}`,
    area_range: `${state.area.from ?? "any"} - ${state.area.to ?? "any"}`,
    amenities: state.amenities?.join(", "),
    virtual_tour_status: state.view,
  };
};

export const propertyToECommerceItem = (
  property: Property,
  index: number,
  listId: string,
  listName: string
) => {
  const availableTours = [];

  if (property.videoTourUrl) {
    availableTours.push("video tour");
  }
  if (property.view360Url) {
    availableTours.push("360 view");
  }
  return {
    item_id: property.id,
    item_name: property.description.en.title,
    item_property_type: property.propertyType,
    item_rental_period: property.rentalPeriod,
    item_badges_status: property.isFairPrice ? "on" : "off",
    item_bedroom_count: property.bedrooms,
    item_bathroom_count: property.bathrooms,
    item_area_range: property.size,
    item_amenities: property.amenities.join(", "),
    item_virtual_tour_status: availableTours,
    agent_id: property.agent.id,
    broker_id: property.agency.id,
    tower_id: property.buildingName,
    project_id: property.referenceNumber,
    affiliation: "Web", // web_desktop or web_mobile
    coupon: "", // dynamic
    discount: 0.0,
    index: index,
    item_brand: "Sakani",
    item_category: property.listingType,
    item_category2: property.propertyType,
    item_category3: property.community,
    item_category4: property.subCommunity,
    item_list_id: listId,
    item_list_name: listName,
    location_id: property.buildingName,
    price: property.price,
    quantity: 1,
  };
};

export const propertiesToViewItemECommerce = (
  properties: Array<Property>,
  category: string
) => {
  return {
    item_list_id: category,
    item_list_name: category,
    items: properties.map((property, i) =>
      propertyToECommerceItem(property, i, category, category)
    ),
  };
};

export const propertyToViewEcommerce = (
  property: Property,
  category: string
) => {
  return {
    currency: "AED",
    value: property.price,
    items: [propertyToECommerceItem(property, 0, category, category)],
  };
};

export const propertyToTransactionEcommerce = (
  transactionId: string,
  property: Property,
  category: string
) => {
  return {
    transaction_id: transactionId,
    value: property.price,
    tax: 0,
    shipping: 0,
    currency: "AED",
    items: [propertyToECommerceItem(property, 0, category, category)],
  };
};
